import React, { useEffect, useState } from 'react';
import axios from 'axios';
import calculateSpeedAndPace from '../utils/calculateSpeedAndPace';
import { calculateTSSWithTerrain } from '../utils/tssUtils';
import calculateDesnivelAcumulado from '../utils/calculateDesnivelAcumulado';
import './styles/Summary.css';
import { useTranslation } from '../i18n';
import API_BASE_URL from '../config/api';


if (typeof window !== "undefined") {
}

const API_BASE = API_BASE_URL;

const formatTSS = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : null;
};

const isDebug = () =>
  (typeof window !== 'undefined' && window.__TSS_DEBUG__ === true);

const dbg = (...args) => {
  if (isDebug()) console.log('[Summary]', ...args);
};

const Summary = ({ fileId, completed }) => {
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const convertSecondsToTime = (seconds) => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const calculateVO2Max = (distanceKm, timeSec, sport = '') => {
    if (!distanceKm || !timeSec) return null;
    const speed = distanceKm / (timeSec / 3600);

    if (sport.includes('run') || sport.includes('trail')) {
      return Math.round((15.3 * speed) / (timeSec / (distanceKm * 60))) || null;
    } else if (sport.includes('bike')) {
      return Math.round(speed * 10) || null;
    } else {
      return Math.round(15 * speed) || null;
    }
  };

  useEffect(() => {
    if (!fileId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(`${API_BASE}/api/fit/parsed-data/${fileId}`);

        const rawSession = res.data.session || res.data.sessions;
        const session = Array.isArray(rawSession) ? rawSession[0] : rawSession || {};
        const activity = res.data.activity || {};
        const zonesTarget = res.data.zones_target || {}; // puede ser objeto o array
        const records = res.data.records || [];

        const sport = (session.sport || '').toLowerCase();
        const distanceKm = (session.total_distance || 0) / 1000;

        // threshold dinámico para desnivel acumulado
        let threshold = 3;
        if (sport.includes('trail') || sport.includes('run') || sport.includes('walk')) {
          threshold = 2;
        } else if (sport.includes('bike') || sport.includes('cycling')) {
          threshold = 3;
        } else if (sport.includes('hike') || sport.includes('climb')) {
          threshold = 1.5;
        } else if (sport.includes('swim')) {
          threshold = 0;
        }

        const elevationGain = calculateDesnivelAcumulado(records, threshold);

        // Armar activityData para tssUtils
        const activityData = {
          ...res.data,
          activity: {
            ...activity,
            distance: distanceKm,
            totalTimerTime: activity.total_timer_time, // <- tssUtils lee totalTimerTime
          },
          sessions: {
            ...session,
            sport: session.sport || activity.sport || 'running',
            avgHeartRate: session.avg_heart_rate,
          },
          zones_target: zonesTarget,
          user: {
            hrMax: res.data?.profile?.hr_max ?? res.data?.profile?.hrMax ?? null,
            runningThresholdPaceSec: res.data?.profile?.runningThresholdPaceSec ?? null,
          },
        };

        // ✅ VP injection (robusto + fallback FORZADO)
        // Prioridad:
        // 1) window.__TP_THRESHOLD_PACE__
        // 2) localStorage "tp_threshold_pace"
        // 3) defaultVP si window.__TP_USE_DEFAULT_VP__ === true
        // 4) FORCED_VP si window.__TP_FORCE_VP__ === true (por defecto true para test)
        const win = (typeof window !== 'undefined') ? window : null;

        const useDefaultVP = win?.__TP_USE_DEFAULT_VP__ === true;
        const defaultVP = "07:30";

        // 👇 fallback final para que NUNCA quede null en test
        const forceVP = (win?.__TP_FORCE_VP__ === undefined) ? true : (win?.__TP_FORCE_VP__ === true);
        const FORCED_VP = "05:17"; // <-- ajustá este para calibrar con TrainingPeaks

        let vpFromWindow = win?.__TP_THRESHOLD_PACE__ ?? null;

        // 2) si no hay en window, intentar localStorage
        let vpFromStorage = null;
        try {
          vpFromStorage = win?.localStorage?.getItem("tp_threshold_pace") ?? null;
        } catch (_) {}

        // normalizar
        vpFromWindow = (vpFromWindow && String(vpFromWindow).trim().length > 0)
          ? String(vpFromWindow).trim()
          : null;

        vpFromStorage = (vpFromStorage && String(vpFromStorage).trim().length > 0)
          ? String(vpFromStorage).trim()
          : null;

        // VP desde perfil del atleta (trainingzones BD)
        const vpFromProfile = activityData.user?.runningThresholdPaceSec
          ? null // se pasa directo como segundos, no como string
          : null;
        let vp = vpFromWindow || vpFromStorage || (useDefaultVP ? defaultVP : null);
        // Si no hay VP en string, usar el del perfil en segundos directamente
        if (!vp && activityData.user?.runningThresholdPaceSec > 0) {
          activityData.user.runningThresholdPaceSec = activityData.user.runningThresholdPaceSec;
        }

        // si viene de window, persistir para próximos reloads
        if (vpFromWindow) {
          try {
            win?.localStorage?.setItem("tp_threshold_pace", vpFromWindow);
          } catch (_) {}
        }

        // 4) fallback forzado DESHABILITADO - usar solo VP real del perfil
        // if (!vp && forceVP) { vp = FORCED_VP; }

        if (vp) {
          activityData.user.runningThresholdPace = vp;
        }

        dbg('VP injection', {
          vpFromWindow, vpFromStorage, useDefaultVP, defaultVP,
          forceVP, FORCED_VP,
          vpApplied: activityData.user.runningThresholdPace
        });

        // Guardar debug sin depender de console.log
        if (typeof window !== "undefined") {
          window.__TSS_DEBUG_LAST__ = {
            vpFromWindow: vpFromWindow ?? null,
            vpFromStorage: vpFromStorage ?? null,
            useDefaultVP,
            defaultVP,
            forceVP,
            FORCED_VP,
            vpApplied: activityData.user?.runningThresholdPace ?? null,
            totalTimerTime: activityData.activity?.totalTimerTime ?? null,
            sport: activityData.sessions?.sport ?? null,
            avgPace_before: activityData.sessions?.avgPace ?? null,
          };
        }

        // Calcular paces/speeds
        const { avgSpeed: avgSpd, maxSpeed: maxSpd, avgPace, maxPace } =
          calculateSpeedAndPace(activityData);

        // Inyectar avgPace para rTSS
        activityData.sessions.avgPace = avgPace;

        if (typeof window !== "undefined") {
          window.__TSS_DEBUG_LAST__.avgPace_after = avgPace ?? null;
        }

        dbg('TSS inputs', {
          totalTimerTime: activityData.activity.totalTimerTime,
          sport: activityData.sessions.sport,
          avgPace: activityData.sessions.avgPace,
          runningThresholdPace: activityData.user?.runningThresholdPace,
        });

        // --- TSS: prioridad al que viene de ActivitySummary (completed) ---
        const completedTSSRaw = Number(completed?.tss);
        const completedTSS = (Number.isFinite(completedTSSRaw) && completedTSSRaw > 0 && completedTSSRaw < 1000) ? formatTSS(completedTSSRaw) : null;
        const tssResult = calculateTSSWithTerrain(activityData);
        const fallbackTSS = formatTSS(tssResult?.tss);

        if (typeof window !== "undefined") {
          window.__TSS_DEBUG_LAST__.tssResult = tssResult ?? null;
          window.__TSS_DEBUG_LAST__.completedTSS = completedTSS ?? null;
          window.__TSS_DEBUG_LAST__.fallbackTSS = fallbackTSS ?? null;
        }

        dbg('TSS result', tssResult);

        const finalTSS = completedTSS ?? fallbackTSS;
        const tssMessage = tssResult?.message;

        const vo2max = calculateVO2Max(distanceKm, activity.total_timer_time, sport);

        setData({
          date: new Date(activity.timestamp).toLocaleString(),
          sport: `${session?.sport || ''}${session?.sub_sport ? ' - ' + session.sub_sport : ''}`,
          duration: convertSecondsToTime(activity.total_timer_time),
          distance: distanceKm.toFixed(2),
          ascent: session.total_ascent,
          descent: session.total_descent,
          elevationGain: elevationGain,
          calories: session.total_calories,
          avgHR: session.avg_heart_rate,
          maxHR: session.max_heart_rate,

          tss: finalTSS ?? t('summary.notAvailable'),
          tssMessage: tssMessage || t('summary.notAvailable'),

          vo2max: vo2max ?? t('summary.notAvailable'),
          trainingEffect: session.total_training_effect ?? '-',
          anaerobicEffect: session.total_anaerobic_effect ?? '-',
          ftp: zonesTarget.functional_threshold_power ?? zonesTarget.ftp ?? '-',
          thr: zonesTarget.threshold_heart_rate ?? zonesTarget.thresholdHeartRate ?? '-',
          avgPace: avgPace ?? t('summary.notAvailable'),
          maxPace: maxPace ?? t('summary.notAvailable'),
          avgSpeed: avgSpd ?? t('summary.notAvailable'),
          maxSpeed: maxSpd ?? t('summary.notAvailable'),
        });
      } catch (err) {
        console.error('❌ Error al cargar los datos:', err);
        setError(t('summary.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fileId, completed, t]);

  if (loading) return <div>{t('summary.loading')}</div>;
  if (error) return <div>{error}</div>;
  if (!data) return <div>{t('summary.noData')}</div>;

  return (
    <div className="summary-container">
      <h4 className="mb-3" style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('summary.title')}</h4>
      <div className="summary-grid">
        <div className="summary-item"><strong><i className="bi bi-calendar-event"></i> {t('summary.date')}:</strong><p>{data.date}</p></div>
        <div className="summary-item"><strong><i className="bi bi-person-running"></i> {t('summary.sport')}:</strong><p>{data.sport}</p></div>
        <div className="summary-item"><strong><i className="bi bi-clock-history"></i> {t('summary.duration')}:</strong><p>{data.duration}</p></div>
        <div className="summary-item"><strong><i className="bi bi-rulers"></i> {t('summary.distance')}:</strong><p>{data.distance} km</p></div>

        <div className="summary-item"><strong><i className="bi bi-fire"></i> {t('summary.calories')}:</strong><p>{data.calories} kcal</p></div>
        <div className="summary-item"><strong><i className="bi bi-arrow-up"></i> {t('summary.ascent')}:</strong><p>{data.ascent} m</p></div>
        <div className="summary-item"><strong><i className="bi bi-arrow-down"></i> {t('summary.descent')}:</strong><p>{data.descent} m</p></div>
        <div className="summary-item"><strong><i className="bi bi-graph-up"></i> {t('summary.elevationGain')}:</strong><p>{data.elevationGain} m</p></div>

        <div className="summary-item"><strong><i className="bi bi-wind"></i> VO₂ máx:</strong><p>{data.vo2max}</p></div>
        <div className="summary-item"><strong><i className="bi bi-beaker"></i> TSS:</strong><p>{data.tss}</p></div>

        {/* Si querés ver el motivo cuando sea No disponible, descomentá: */}
        {/* <div className="summary-item"><strong>{t("summary.debugTss")}</strong><p>{data.tssMessage}</p></div> */}

        <div className="summary-item"><strong><i className="bi bi-activity"></i> TE:</strong><p>{data.trainingEffect}</p></div>
        <div className="summary-item"><strong><i className="bi bi-graph-up-arrow"></i> TEA:</strong><p>{data.anaerobicEffect}</p></div>

        <div className="summary-item"><strong><i className="bi bi-bolt"></i> FTP:</strong><p>{data.ftp} W</p></div>
        <div className="summary-item"><strong><i className="bi bi-bullseye"></i> THR:</strong><p>{data.thr} bpm</p></div>
        <div className="summary-item"><strong><i className="bi bi-heart-pulse"></i> {t('summary.avgHR')}:</strong><p>{data.avgHR} bpm</p></div>
        <div className="summary-item"><strong><i className="bi bi-heart-fill"></i> {t('summary.maxHR')}:</strong><p>{data.maxHR} bpm</p></div>

        <div className="summary-item"><strong><i className="bi bi-speedometer"></i> {t('summary.avgPace')}:</strong><p>{data.avgPace}</p></div>
        <div className="summary-item"><strong><i className="bi bi-speedometer2"></i> {t('summary.maxPace')}:</strong><p>{data.maxPace}</p></div>
        <div className="summary-item"><strong><i className="bi bi-tachometer"></i> {t('summary.avgSpeed')}:</strong><p>{data.avgSpeed} km/h</p></div>
        <div className="summary-item"><strong><i className="bi bi-tachometer2"></i> {t('summary.maxSpeed')}:</strong><p>{data.maxSpeed} km/h</p></div>
      </div>
    </div>
  );
};

export default Summary;